import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatEUR, formatCrypto } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { BrandMonogram } from "../Brand";
import { LEGAL } from "../../lib/legalConfig";
import { Button } from "../ui/button";
import { Printer, ArrowLeft, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Print-friendly order receipt.
 *
 * Surfaces the three identifiers a customer needs for a future dispute or
 * tax audit:
 *   1. The XGate transaction id (xgate_transaction_id)
 *   2. The on-chain tx hash (chain_txid)
 *   3. The customer's stable xgate_customer_id
 *
 * Plus the human-readable bits (asset, network, amounts, dates, rates).
 *
 * Print CSS hides the surrounding chrome so `window.print()` produces a
 * single clean A4 page.
 */
export default function ReceiptPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/v1/orders/${orderId}`)
      .then((r) => { if (alive) setOrder(r.data); })
      .catch(() => { if (alive) toast.error("Could not load this order"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orderId]);

  const copy = async (val, label) => {
    if (!val) return;
    try { await navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };

  const blockExplorerUrl = (network, txid) => {
    if (!txid) return null;
    if (/Ethereum/i.test(network) || /Polygon/i.test(network) ||
        /Arbitrum/i.test(network) || /Base/i.test(network) ||
        /Optimism/i.test(network) || /BNB/i.test(network) || /Avalanche/i.test(network)) {
      // Heuristic only — real implementation should match per network
      const base = { Ethereum: "https://etherscan.io", Polygon: "https://polygonscan.com",
                     Arbitrum: "https://arbiscan.io", "Base": "https://basescan.org",
                     Optimism: "https://optimistic.etherscan.io",
                     "BNB Smart Chain": "https://bscscan.com",
                     "Avalanche C-Chain": "https://snowtrace.io" };
      for (const k of Object.keys(base)) if (network.includes(k)) return `${base[k]}/tx/${txid}`;
      return `https://etherscan.io/tx/${txid}`;
    }
    if (/Tron/i.test(network)) return `https://tronscan.org/#/transaction/${txid}`;
    if (/Solana/i.test(network)) return `https://solscan.io/tx/${txid}`;
    if (/Bitcoin/i.test(network)) return `https://mempool.space/tx/${txid}`;
    return null;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading receipt…</div>;
  if (!order) return (
    <div className="min-h-screen flex items-center justify-center">
      <Button onClick={() => navigate("/account/orders")} variant="outline" className="rounded-xl">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to orders
      </Button>
    </div>
  );

  const explorer = blockExplorerUrl(order.network, order.chain_txid);

  return (
    <div className="receipt-root bg-white min-h-screen text-zinc-900" data-testid="receipt-page">
      {/* Page chrome — hidden on print */}
      <div className="no-print bg-muted/30 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button onClick={() => navigate(`/account/orders`)} variant="ghost" size="sm" className="gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to orders
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => window.print()} className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                    data-testid="print-receipt-btn">
              <Printer className="w-4 h-4" /> Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Print sheet */}
      <main className="max-w-3xl mx-auto px-8 py-10 receipt-sheet">
        <div className="flex items-start justify-between border-b border-zinc-200 pb-5 mb-6">
          <div className="flex items-center gap-3">
            <BrandMonogram size={48} color="#4F46FF" />
            <div>
              <div className="font-display text-2xl font-extrabold tracking-tight">Xchangely</div>
              <div className="text-xs text-zinc-500">Order receipt</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Order ref</div>
            <div className="font-mono text-sm font-bold" data-testid="receipt-reference">{order.reference || order.id.slice(0, 12).toUpperCase()}</div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mt-3">Issued</div>
            <div className="text-xs">{new Date().toLocaleString("en-IE")}</div>
          </div>
        </div>

        {/* Summary */}
        <section className="grid grid-cols-2 gap-x-6 gap-y-4 mb-6">
          <ReceiptField label="Customer name" value={user?.name} />
          <ReceiptField label="Account email" value={user?.email} />
          <ReceiptField label="Asset purchased" value={order.asset} />
          <ReceiptField label="Network" value={order.network} />
          <ReceiptField label="Crypto delivered" value={formatCrypto(order.receive_amount) + " " + order.asset} mono />
          <ReceiptField label="EUR paid" value={formatEUR(order.fiat_amount)} mono />
          <ReceiptField label="Payment method" value={(order.payment_method || "").toUpperCase()} />
          <ReceiptField label="Order placed" value={new Date(order.created_at).toLocaleString("en-IE")} />
          <ReceiptField label="Order status" value={(order.status || "").replace(/_/g, " ").toUpperCase()} />
          {order.completed_at && (
            <ReceiptField label="Delivered" value={new Date(order.completed_at).toLocaleString("en-IE")} />
          )}
        </section>

        <h2 className="font-display text-base font-extrabold mb-3 border-b border-zinc-200 pb-1.5">Wallet</h2>
        <div className="text-xs font-mono break-all bg-zinc-50 p-3 rounded-md mb-6" data-testid="receipt-wallet">{order.wallet_address}</div>

        <h2 className="font-display text-base font-extrabold mb-3 border-b border-zinc-200 pb-1.5">Verifiable identifiers</h2>
        <p className="text-xs text-zinc-500 mb-3">
          Keep these for your records. They are the references you'll need for any
          future dispute, support enquiry, or tax-audit reconciliation.
        </p>
        <div className="space-y-2.5 mb-6">
          <IdentifierRow
            label="Customer ID (XGate)"
            value={user?.xgate_customer_id}
            onCopy={() => copy(user?.xgate_customer_id, "Customer ID")}
            testid="receipt-xgate-customer-id"
          />
          <IdentifierRow
            label="Payment transaction (XGate)"
            value={order.xgate_transaction_id}
            onCopy={() => copy(order.xgate_transaction_id, "XGate txn id")}
            testid="receipt-xgate-tx"
          />
          <IdentifierRow
            label="On-chain transaction hash"
            value={order.chain_txid}
            onCopy={() => copy(order.chain_txid, "Chain tx hash")}
            link={explorer}
            testid="receipt-chain-tx"
          />
        </div>

        {/* Footer / legal — lawyer-approved compliance block */}
        <div className="border-t border-zinc-200 pt-4 mt-6 text-[10px] text-zinc-500 leading-relaxed space-y-1.5"
             data-testid="receipt-compliance-block">
          <div>
            <strong className="text-zinc-700">Xchangely</strong> — a virtual-currency on-ramp.
          </div>
          <div>
            The virtual IBAN and crypto swap are facilitated through our regulated payment partner. This service does not
            provide investment advice; crypto-asset values can go down as well as up. Crypto purchases are
            non-refundable. For support contact {LEGAL.SUPPORT_EMAIL}.
          </div>
        </div>
      </main>

      {/* Print CSS — invisible on screen, applied on print */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .receipt-sheet { padding-top: 0 !important; padding-bottom: 0 !important; }
        }
        @page { size: A4; margin: 14mm 12mm; }
      `}</style>
    </div>
  );
}

const ReceiptField = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">{label}</div>
    <div className={`text-sm font-medium ${mono ? "font-mono tabular-nums" : ""}`} data-testid={`receipt-${label.replace(/\W+/g, "-").toLowerCase()}`}>
      {value || "—"}
    </div>
  </div>
);

const IdentifierRow = ({ label, value, onCopy, link, testid }) => (
  <div className="grid grid-cols-[160px_1fr_auto] items-center gap-3 text-xs" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">{label}</div>
    <code className="font-mono text-[11px] bg-zinc-50 px-2 py-1.5 rounded-md break-all">{value || "—"}</code>
    <div className="flex items-center gap-1 no-print">
      {value && (
        <button onClick={onCopy}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-zinc-100 text-zinc-500"
                aria-label="Copy">
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer"
           className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-zinc-100 text-zinc-500"
           aria-label="View on block explorer">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  </div>
);
